import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faImage,
  faSpinner,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "../../../../hooks/useFetch";

const WebpImgUploader = ({
  label,
  value = "",
  rounded = false,
  uploadType = "general",
  uploadUrl,
  token,
  extraHeaders = {},
  onChange,
  onClose,
  maxSizeKB = 100,
  showWebpHelp = true,
}) => {
  const authState = useSelector((state) => state.authReducer);
  const userCode = authState?.user?.think5whyscode;
  const inputRef = useRef(null);
  const [fetchData] = useFetch();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(value);
  const [message, setMessage] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [converting, setConverting] = useState(false);

  const chooseFile = () => {
    inputRef.current.click();
  };

  const convertToWebp = (file) => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              reject(new Error("Conversion failed"));
              return;
            }
            const webpFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, "") + ".webp",
              { type: "image/webp" }
            );
            resolve(webpFile);
          },
          "image/webp",
          0.85
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Could not read image"));
      };

      img.src = objectUrl;
    });
  };


  // const uploadImage = (e) => {
  //   const file = e.target.files[0];

  //   if (!file) return;
  //   setSelectedFile(file);
  //   setPreview(URL.createObjectURL(file));
  //   setMessage("");
  // };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMessage("");

    if (file.type === "image/webp") {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      return;
    }

    if (file.type === "image/jpeg" || file.type === "image/png") {
      try {
        setConverting(true);
        const webpFile = await convertToWebp(file);
        setSelectedFile(webpFile);
        setPreview(URL.createObjectURL(webpFile));
      } catch (err) {
        setMessage("❌ Could not convert this image. Please try a different file.");
      } finally {
        setConverting(false);
      }
      return;
    }

    setMessage("❌ Please upload a JPG, PNG, or WebP image.");
  };

  const uploadNow = async () => {
    if (!selectedFile) return;
    const allowedTypes = ["image/webp"];
    const maxSize = maxSizeKB * 1024; // 100 KB default -- maxWidth 256, maxHeight 256

    if (!allowedTypes.includes(selectedFile.type)) {
      setMessage("❌ Please upload a WebP (.webp) image only.");
      return;
    }

    if (selectedFile.size > maxSize) {
      setMessage(`❌ Image size should be less than ${maxSizeKB} KB.`);
      return;
    }

    const body = new FormData();
    body.append("file", selectedFile);
    body.append("userCode", userCode);

    try {
      setUploading(true);

      const config = {
        url: `/uploads${uploadUrl}`,
        method: "post",
        data: body,
        headers: {
          Authorization: token,
          "x-user-code": userCode,
          ...extraHeaders,
        },
      };

      const response = await fetchData(config);
      if (response?.file?.filename) {
        const filename = response.file.filename;
        const displayUrl = response.url || response.file.url;
        onChange(filename, displayUrl);
        setMessage("✅ Profile photo uploaded successfully.");
        setTimeout(() => setMessage(""), 3000);
        setSelectedFile(null);
        inputRef.current.value = "";
        setPreview(displayUrl || filename);
      } else {
        setMessage("❌ Upload failed. Please try again.");
      }
    } catch {
      setMessage("❌ Unable to upload photo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-start justify-start p-4 mb-4 mt-4 border-y-2 border-yellow-200 rounded-lg">
      <div className="text-sm lg:text-lg font-semibold text-yellow-200">{label}</div>
      <div className="mt-2 text-xs text-gray-400">
        {/* Accepted format: <b>WebP</b> • Maximum size: <b>{maxSizeKB} KB</b> */}
        Accepted format: <b>JPG, PNG or WebP</b> (auto-converted to WebP) • Maximum size after conversion: <b>{maxSizeKB} KB</b>
      </div>
      <div
        onClick={chooseFile}
        className={`mt-2 mx-auto cursor-pointer border overflow-hidden bg-gray-700 flex items-center justify-center ${
          rounded ? "w-36 h-36 rounded-full mx-auto" : "h-44 rounded-xl"
        }`}
        
      >
        {/* {uploading ? ( */}
        {uploading || converting ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-4xl" />
        ) : preview || value ? (
          <img
            src={preview || value}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center">
            <FontAwesomeIcon
              icon={faImage}
              className="text-5xl text-gray-100 mb-3"
            />

            <div className="text-sm font-medium">Click to Upload</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap justify-center gap-2 mt-4">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white w-fit px-3 py-2 rounded-lg text-sm"
          >
            ✕ Close
          </button>
        )}

        {/* <button
          type="button"
          onClick={chooseFile}
          className="bg-pink-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faCamera} />
          Apply Change
        </button>

        {selectedFile && (
          <button
            type="button"
            disabled={uploading}
            onClick={uploadNow}
            className="bg-green-600 hover:bg-green-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            {uploading ? "Uploading..." : "⬆ Upload Photo"}
          </button>
        )} */}
        <button
          type="button"
          disabled={converting}
          onClick={chooseFile}
          className="bg-pink-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
        >
          <FontAwesomeIcon icon={faCamera} />
          {converting ? "Converting..." : "Apply Change"}
        </button>

        {selectedFile && (
          <button
            type="button"
            disabled={uploading || converting}
            onClick={uploadNow}
            className="bg-green-600 hover:bg-green-700 text-white w-fit px-3 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {uploading ? "Uploading..." : "⬆ Upload Photo"}
          </button>
        )}
      </div>
      {message && (
        <div
          className={`mt-3 text-sm font-medium ${
            message.startsWith("✅") ? "text-green-600" : "text-red-500"
          }`}
        >
          {message}
        </div>
      )}      
      {showWebpHelp && (
        <div className="flex flex-col w-full mt-3">
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="px-2 py-1 text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faCircleQuestion} />
            WebP How To
          </button>

          {showHelp && (
            <div className="mt-2 text-xs bg-gray-800 border border-gray-700 rounded-lg p-3 text-gray-200 leading-6">
              <div className="font-semibold text-green-400 mb-2">Why WebP?</div>

              <div>
                WebP images are much smaller while keeping excellent quality,
                making uploads faster and saving your mobile data.
              </div>

              <ol className="list-decimal ml-5 mt-2 space-y-1">
                <li className="text-gray-100">Select your JPG, JPEG or PNG image.</li>
                <li className="text-gray-100">Convert it to WebP.</li>
                <li className="text-gray-100">Download the WebP image on your device and upload it here.</li>
              </ol>

              <div className="mt-3">
                Search Google for any free converter like:
              </div>

              <div className="mt-1 text-blue-300">
                • Squoosh
                <br />
                • CloudConvert
                <br />
                • Convertio
                <br />• TinyIMG
              </div>
            </div>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/*"
        onChange={uploadImage}
      />
    </div>
  );
};

export default WebpImgUploader;
